import React, { useMemo, useState, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Box,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { getCountries, getCountryCallingCode } from "react-phone-number-input/input";
// NOT "react-phone-number-input/flags" — its ESM entry only re-exports a
// `default`, so `import * as flags` resolves every per-country name
// (flags.KY, flags.US, …) to undefined under webpack/CRA's ESM resolution
// (only Node's CommonJS `require()` happens to forward the named exports
// too, which is why this looked fine in a quick node -e check but not in
// the actual built app). Importing directly from the flag icon package
// react-phone-number-input itself wraps sidesteps that entirely.
import * as flags from "country-flag-icons/react/3x2";
import { AsYouType, parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import countryLabels from "react-phone-number-input/locale/en.json";

const COUNTRY_OPTIONS = getCountries()
  .map((code) => ({ code, callingCode: getCountryCallingCode(code), label: countryLabels[code] || code }))
  .sort((a, b) => a.label.localeCompare(b.label));

const DEFAULT_COUNTRY = "KY";

// Same custom-styled TextField used everywhere else in the app, just with a
// flag/country-code picker as its start adornment instead of a plain icon —
// this replaced a bare <TextField> that accepted literally any string
// (including an email address) as a "phone number". Stores/emits the number
// in E.164 (e.g. "+13451234567"), same shape User.phone already used.
// Validity is libphonenumber-js's standard per-country rules — no
// Cayman-specific special-casing, same as every other country.
const PhoneNumberField = ({
  value,
  onChange,
  label = "Phone number",
  required,
  fullWidth,
  sx,
  ...rest
}) => {
  const [country, setCountry] = useState(() => {
    const parsed = value && parsePhoneNumberFromString(value);
    return parsed?.country || DEFAULT_COUNTRY;
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [touched, setTouched] = useState(false);

  const nationalText = useMemo(() => {
    if (!value) return "";
    const parsed = parsePhoneNumberFromString(value);
    return parsed ? parsed.formatNational() : value;
  }, [value]);

  const [text, setText] = useState(nationalText);
  useEffect(() => {
    setText(nationalText);
  }, [nationalText]);

  const callingCode = getCountryCallingCode(country);
  const selected = COUNTRY_OPTIONS.find((c) => c.code === country);
  const SelectedFlag = flags[country];

  const emitFromDigits = (digits, forCountry) => {
    if (!digits) {
      onChange("");
      return;
    }
    const parsed = parsePhoneNumberFromString(digits, forCountry);
    onChange(parsed ? parsed.number : `+${getCountryCallingCode(forCountry)}${digits}`);
  };

  const handleTextChange = (e) => {
    const raw = e.target.value;
    setText(new AsYouType(country).input(raw));
    emitFromDigits(raw.replace(/\D/g, ""), country);
  };

  const handleCountrySelect = (code) => {
    setCountry(code);
    setAnchorEl(null);
    emitFromDigits(text.replace(/\D/g, ""), code);
  };

  const invalid = touched && !!value && !isValidPhoneNumber(value);

  return (
    <TextField
      label={label}
      required={required}
      fullWidth={fullWidth}
      sx={sx}
      value={text}
      onChange={handleTextChange}
      onBlur={() => setTouched(true)}
      error={invalid}
      helperText={invalid ? "Enter a valid phone number for the selected country" : undefined}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  pr: 1,
                  mr: 0.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                }}
              >
                {SelectedFlag && <SelectedFlag title={selected?.label} style={{ width: 20, height: 14 }} />}
                <Typography variant="body2" color="text.secondary">
                  +{callingCode}
                </Typography>
                <ArrowDropDown sx={{ fontSize: 18, color: "text.disabled" }} />
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                slotProps={{ paper: { sx: { maxHeight: 340 } } }}
              >
                {COUNTRY_OPTIONS.map((c) => {
                  const Flag = flags[c.code];
                  return (
                    <MenuItem key={c.code} selected={c.code === country} onClick={() => handleCountrySelect(c.code)}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {Flag && <Flag title={c.label} style={{ width: 20, height: 14 }} />}
                      </ListItemIcon>
                      <ListItemText primary={c.label} secondary={`+${c.callingCode}`} />
                    </MenuItem>
                  );
                })}
              </Menu>
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  );
};

export default PhoneNumberField;
